import { React, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles/";
import {
	useMarkTransactionAsConnectedWithAssetOrLiabilityMutation,
	useGetWalletValueOnDate,
} from "../../services/queryHooks";
import { useTranslation } from "react-i18next";
import { Loading } from "../../components/common/Loading";
import { validateMarkAsLinkedWallet } from "../../utils/transactionValidation";

export default function MarkAsModal({ isOpen, onClose, wallets, transaction }) {
	const theme = useTheme();
	const { t } = useTranslation();
	const markTransactionAsConnectedWithAssetOrLiability =
		useMarkTransactionAsConnectedWithAssetOrLiabilityMutation();

	const [selectedType, setSelectedType] = useState("");
	const [walletChoices, setWalletChoices] = useState([]);
	const [selectedWallet, setSelectedWallet] = useState("");

	const {
		data: walletValueOnDateQuery,
		isPending: walletValueOnDateQueryPending,
	} = useGetWalletValueOnDate(selectedWallet, transaction.createdAt);
	const [errorType, setErrorType] = useState("");
	const [errorWallet, setErrorWallet] = useState("");

	const handleTypeChange = (event) => {
		setSelectedType(event.target.value);
		setSelectedWallet("");
		setWalletChoices(
			wallets.filter(
				(item) =>
					item.type === event.target.value &&
					item.currency === transaction.currency
			)
		);
	};

	const areWalletsAvailableForType = (type) => {
		return wallets.some(
			(item) =>
				item.type === type && item.currency === transaction.currency
		);
	};

	const handleWalletChange = (event) => {
		setSelectedWallet(event.target.value);
	};

	const validateInputs = () => {
		setErrorType("");
		setErrorWallet("");

		let hasErrors = false;

		if (!selectedType) {
			setErrorType(t("transactionMarkAsTypeError"));
			hasErrors = true;
		}
		if (selectedType && !selectedWallet) {
			setErrorWallet(t("transactionMarkAsWalletError"));
			hasErrors = true;
		}

		if (selectedWallet) {
			const wallet = wallets.find((w) => w._id === selectedWallet);

			const walletError = validateMarkAsLinkedWallet(
				selectedType,
				transaction.amount,
				wallet,
				walletValueOnDateQuery.data?.value,
				transaction.createdAt,
				walletValueOnDateQuery.isLoading,
				walletValueOnDateQuery.error,
				t
			);

			if (walletError) {
				setErrorWallet(walletError);
				hasErrors = true;
			}
		}

		return !hasErrors;
	};

	const submitForm = async (event) => {
		event.preventDefault();
		if (errorType || errorWallet) {
			return;
		}

		markTransactionAsConnectedWithAssetOrLiability.mutate({
			transactionId: transaction._id,
			investmentDebtPayoff: selectedWallet,
		});
		handleClose();
	};

	const handleClose = () => {
		setSelectedType("");
		setSelectedWallet("");

		setErrorType("");
		setErrorWallet("");
		onClose();
	};

	const liabilityLabel =
		transaction.amount > 0 ? t("increaseDebtLiability") : t("debtPayoff");
	const assetLabel =
		transaction.amount > 0 ? t("withdrawFromAsset") : t("investment");

	if (selectedWallet && walletValueOnDateQueryPending) {
		return <Loading />;
	}

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				sx={{ margin: "auto", padding: 0 }}
				disableEnforceFocus
			>
				<DialogTitle>{t("markTransactionAs")}:</DialogTitle>
				<form
					noValidate
					onSubmit={async (event) => {
						submitForm(event);
					}}
				>
					<DialogContent sx={{ width: 500 }}>
						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<InputLabel>{t("type")}</InputLabel>
							<Select
								required
								value={selectedType}
								label={t("type")}
								onChange={handleTypeChange}
								error={!!errorType}
							>
								{areWalletsAvailableForType("asset") ? (
									<MenuItem value={"asset"}>
										{assetLabel}
									</MenuItem>
								) : (
									<Tooltip
										arrow
										placement="top"
										title={
											areWalletsAvailableForType("asset")
												? ""
												: t(
														"noAssetsLiabilitiesForThisType"
													)
										}
									>
										<span>
											<MenuItem
												value={"asset"}
												disabled={
													!areWalletsAvailableForType(
														"asset"
													)
												}
											>
												{assetLabel}
											</MenuItem>
										</span>
									</Tooltip>
								)}
								{areWalletsAvailableForType("liability") ? (
									<MenuItem
										value={"liability"}
										disabled={
											!areWalletsAvailableForType(
												"liability"
											)
										}
									>
										{liabilityLabel}
									</MenuItem>
								) : (
									<Tooltip
										arrow
										placement="top"
										title={
											areWalletsAvailableForType(
												"liability"
											)
												? ""
												: t(
														"noAssetsLiabilitiesForThisType"
													)
										}
									>
										<span>
											<MenuItem
												value={"liability"}
												disabled={
													!areWalletsAvailableForType(
														"liability"
													)
												}
											>
												{liabilityLabel}
											</MenuItem>
										</span>
									</Tooltip>
								)}
							</Select>
						</FormControl>
						{errorType && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errorType}
							</FormHelperText>
						)}

						{selectedType && (
							<FormControl fullWidth sx={{ mt: 2 }} size="small">
								<InputLabel>{t("wallet")}</InputLabel>
								<Tooltip
									placement="top"
									arrow
									title={
										walletChoices.length === 0
											? t(
													"noAssetsLiabilitiesForThisType"
												)
											: ""
									}
								>
									<Select
										required
										value={selectedWallet}
										label={t("wallet")}
										onChange={handleWalletChange}
										error={!!errorWallet}
										disabled={walletChoices.length === 0}
									>
										{walletChoices.map((item) => (
											<MenuItem
												key={item._id}
												value={item._id}
											>
												{item.name}
											</MenuItem>
										))}
									</Select>
								</Tooltip>
							</FormControl>
						)}
						{errorWallet && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errorWallet}
							</FormHelperText>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>{t("cancel")}</Button>
						<Button
							type="submit"
							variant="contained"
							onClick={validateInputs}
						>
							{t("save")}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
